import { useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom'; // React Router for navigation
import { Button, Input, Form, notification, Typography, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRequestOTP, useResetPassword } from '../Hooks/Index';
import { GlobalOutlined } from '@ant-design/icons';

const { Title } = Typography;

const ForgetPassword = () => {
  const { t, i18n } = useTranslation('forgetPassword'); // Translation hook
  const navigate = useNavigate(); // Navigation hook
  const location = useLocation(); // Get current location
  const searchParams = new URLSearchParams(location.search); // Parse query parameters
  const token = searchParams.get('token'); // Extract `token` from URL

  const [isVerificationSent, setIsVerificationSent] = useState(false); // State to track OTP request
  const [form] = Form.useForm(); // Ant Design form instance

  // Hook to request OTP
  const requestOTPMutation = useRequestOTP({
    onSuccess: () => {
      setIsVerificationSent(true); // Set verification state on success
    },
    onError: (error) => {
      notification.error({
        message: error?.response?.data?.message || t('otpRequestFailed'), // Show error notification
      });
    },
  });

  // Hook to reset password
  const resetPasswordMutation = useResetPassword({
    onSuccess: () => {
      notification.success({ message: t('passwordResetSuccess') }); // Show success notification
      navigate('/'); // Redirect to login page
    },
    onError: (error) => {
      notification.error({
        message: error?.response?.data?.message || t('passwordResetFailed'), // Show error notification
      });
    },
  });

  // Pre-fill token field if present in URL
  useEffect(() => {
    if (token) {
      // No longer set token in form field
    }
  }, [token]);

  // Request OTP
  const handleRequestOTP = async () => {
    try {
      const values = await form.validateFields(['email']); // Validate email field
      requestOTPMutation.mutate({ email: values.email }); // Trigger OTP request
    } catch (error) {
      console.error('Email validation failed:', error);
    }
  };

  // Submit New Password
  const handlePasswordReset = async (values) => {
    resetPasswordMutation.mutate({
      token: token, // Use token from state, not form
      new_password: values.newPassword,
      compare_password: values.confirmPassword,
    });
  };

  // Language Switcher
  const changeLanguage = (value) => {
    i18n.changeLanguage(value); // Change the app's language
  };

  return (
    <div>
      {/* Language Switcher */}
      <div className="flex justify-end mb-4">
        <Select
          className="min-w-[140px]"
          defaultValue={i18n.language}
          prefix={<GlobalOutlined className="mr-2" />}
          size="large"
          onChange={changeLanguage}
        >
          <Select.Option value="en">{t('common:langEnglish')}</Select.Option>
          <Select.Option value="tr">{t('common:langTurkish')}</Select.Option>
        </Select>
      </div>

      {/* Page Title */}
      <Title
        className="text-center text-2xl font-semibold text-gray-800"
        level={3}
      >
        {token ? t('resetPassword') : t('forgotPassword')}
      </Title>

      {/* Form */}
      <Form
        form={form}
        layout="vertical"
        className="space-y-4"
        onFinish={token ? handlePasswordReset : handleRequestOTP}
      >
        {/* Email Field */}
        {!token && !isVerificationSent && (
          <Form.Item
            label={t('email')}
            name="email"
            rules={[
              { required: true, message: t('emailRequired') },
              { type: 'email', message: t('invalidEmail') },
            ]}
          >
            <Input placeholder={t('enterEmail')} type="email" />
          </Form.Item>
        )}

        {/* Verification Message */}
        {isVerificationSent && !token && (
          <div className="text-base mt-8" type="secondary">
            {t('verificationSent')} {t('checkEmailAndReset')}
          </div>
        )}

        {/* New Password Fields */}
        {token && (
          <>
            <Form.Item
              label={t('newPassword')}
              name="newPassword"
              rules={[
                { required: true, message: t('passwordRequired') },
                { min: 8, message: t('passwordMinLength') },
                { pattern: /[A-Z]/, message: t('passwordUppercase') },
                { pattern: /\d/, message: t('passwordNumber') },
                { pattern: /[@$!%*?&]/, message: t('passwordSpecialChar') },
              ]}
            >
              <Input.Password placeholder={t('enterNewPassword')} autoComplete="new-password" />
            </Form.Item>

            <Form.Item
              label={t('confirmPassword')}
              name="confirmPassword"
              dependencies={['newPassword']}
              rules={[
                { required: true, message: t('confirmPasswordRequired') },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error(t('passwordMismatch')));
                  },
                }),
              ]}
            >
              <Input.Password placeholder={t('confirmNewPassword')} autoComplete="new-password" />
            </Form.Item>
          </>
        )}

        {/* Submit Button */}
        {!isVerificationSent && (
          <Form.Item>
            {!token ? (
              <Button
                block
                className="mt-4"
                loading={requestOTPMutation.isPending}
                type="primary"
                onClick={handleRequestOTP}
              >
                {t('requestOTP')}
              </Button>
            ) : (
              <Button
                block
                className="mt-4"
                htmlType="submit"
                loading={resetPasswordMutation.isPending}
                type="primary"
              >
                {t('resetPassword')}
              </Button>
            )}
          </Form.Item>
        )}

        {/* Go Back to Login Button */}
        <Button
          block
          className="mt-4"
          type="default"
          onClick={() => navigate('/')} // Redirect to Login Page
        >
          {t('goBackToLogin')}
        </Button>
      </Form>
      
      {/* Footer Links */}
      <div className="mt-8 pt-6 border-t border-gray-200 text-center">
        <div className="flex justify-center space-x-6 text-sm text-gray-600">
          <Link 
            to="/terms-and-conditions" 
            className="hover:text-blue-600 hover:underline"
          >
            {t('termsAndConditions')}
          </Link>
          <Link 
            to="/privacy-policy" 
            className="hover:text-blue-600 hover:underline"
          >
            {t('privacyPolicy')}
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgetPassword;
